// Check if a date is same as today's date
export const isToday = (date: Date | string) => {
  const today = new Date();

  if (typeof date === "string" && !isValidDateString(date)) {
    return false;
  }

  date = new Date(date);

  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

// Check if a string is a valid date string
export const isValidDateString = (date: string) => {
  return !isNaN(new Date(date).getDate());
};

export function diffInMilliseconds(date1: Date, date2: Date) {
  const diffInMilliseconds = Math.abs(date1.getTime() - date2.getTime());

  return diffInMilliseconds;
}

export function diffInSeconds(date1: Date, date2: Date) {
  const diff = diffInMilliseconds(date1, date2);

  return Math.floor(diff / 1000);
}

export function diffInMinutes(date1: Date, date2: Date) {
  const diff = diffInMilliseconds(date1, date2);

  return Math.floor(diff / 1000 / 60);
}

// Formats a date string into a human-readable date (e.g., "January 1, 2024")
export const formatToLongDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Formats a date string into a 24-hour time (e.g., "14:30")
export const formatTo24HourTime = (date: Date | string, timeZone?: string) => {
  if (!date) return "Invalid date";

  if (typeof date === "string") {
    if (!isValidDateString(date)) return "Invalid date";
  }

  // console.log("Formatting date:", date, "with timeZone:", timeZone);

  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timeZone,
  }).format(new Date(date));
};

// Reads a date-only "YYYY-MM-DD" (e.g. an <input type="date"> value) as midnight
// on the user's own clock. Don't use `new Date("YYYY-MM-DD")` for this: it reads
// the day as midnight UTC, which is still the previous day anywhere west of UTC,
// so a New York search for the 16th ran on the 15th. Strings with a time pass
// straight through `new Date`.
export const parseLocalDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return new Date(value);

  const [, year, month, day] = match.map(Number);
  return new Date(year, month - 1, day);
};

// Formats a date as "YYYY-MM-DD" for the user's own day. Don't use
// `toISOString().slice(0, 10)` for this: that is the UTC day, which at local
// midnight in UK summer time is still yesterday.
export const formatLocalDate = (date: Date) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
};
